"use client";

import { observer } from "mobx-react-lite";
import { type FormEvent, useEffect, useState } from "react";

import MODAL_NAMES from "../ModalName";

import {
  AlertCircle,
  X,
  FileText,
  Star,
  AlertTriangle,
  MessageSquare,
} from "lucide-react";
import useIndividualScorecard from "../../../shared/hooks/useIndividualScorecard";
import { useAppContext } from "../../../shared/functions/Context";
import { defaultMeasure, IMeasure } from "../../../shared/models/Measure";
import { IScorecardMetadata } from "../../../shared/models/ScorecardMetadata";
import {
  MAIL_EMAIL,
  MAIL_MIDTERM_RATINGS_DISPUTE_ME,
} from "../../../shared/functions/mailMessages";
import { hideModalFromId } from "../../../shared/functions/ModalShow";

const SubmitScorecardDisputeModal = observer(() => {
  const { api, store, ui } = useAppContext();

  const [measure, setMeasure] = useState<IMeasure>({ ...defaultMeasure });
  const [loading, setLoading] = useState(false);
  const [remainingChar, setRemainingChars] = useState(0);
  const [disputeReason, setDisputeReason] = useState("");
  const [preferredRating, setPreferredRating] = useState<number | undefined>(
    undefined,
  );
  const [disputeType, setDisputeType] = useState("");
  const [additionalComments, setAdditionalComments] = useState("");
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const maxCharCount = 2000;
  const uid = store.auth.meJson?.uid;
  const agreement = useIndividualScorecard(uid);
  const measures = store.measure.getByUid(uid!);

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!disputeReason.trim()) {
      newErrors.disputeReason = "Dispute reason is required";
    } else if (disputeReason.trim().length < 20) {
      newErrors.disputeReason = "Dispute reason must be at least 20 characters";
    }

    if (
      preferredRating !== undefined &&
      (preferredRating < 1 || preferredRating > 5)
    ) {
      newErrors.preferredRating = "Rating must be between 1 and 5";
    }

    if (!disputeType) {
      newErrors.disputeType = "Please select a dispute type";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateForm()) {
      ui.snackbar.load({
        id: Date.now(),
        type: "danger",
        message: "Please fix the errors before submitting",
        timeoutInMs: 5000,
      });
      return;
    }

    const selected = store.measure.selected;
    if (!selected) {
      ui.snackbar.load({
        id: Date.now(),
        type: "danger",
        message: "No measure selected",
        timeoutInMs: 5000,
      });
      return;
    }

    const incompleteReviewError = measures.some(
      (m) => m.asJson.supervisorRating === null,
    );
    if (incompleteReviewError) {
      ui.snackbar.load({
        id: Date.now(),
        type: "danger",
        message: "Incomplete review. Some measures are not rated.",
        timeoutInMs: 10000,
      });
      return;
    }

    const selectedUser = store.auth.meJson;
    if (!selectedUser) {
      ui.snackbar.load({
        id: Date.now(),
        type: "danger",
        message: "User not found.",
        timeoutInMs: 10000,
      });
      return;
    }

    setLoading(true);
    try {
      // Save the dispute data
      await updateDispute(
        measure,
        disputeReason,
        preferredRating,
        disputeType,
        additionalComments,
      );

      // Show success message and close modal immediately after save succeeds
      ui.snackbar.load({
        id: Date.now(),
        type: "success",
        message: "Dispute submitted successfully",
        timeoutInMs: 5000,
      });
      onCancel();

      // Send email in background (non-blocking)
      try {
        const { MY_SUBJECT, MY_BODY } = MAIL_MIDTERM_RATINGS_DISPUTE_ME(
          selectedUser.displayName,
          selectedUser.displayName,
        );
        await api.mail.sendMailCC(
          ["systems@lotsinsights.com"],
          [selectedUser.email!],
          MAIL_EMAIL,
          MY_SUBJECT,
          MY_BODY,
        );
      } catch (emailError) {
        console.error("Email sending failed:", emailError);
        // Optionally notify user that email failed but dispute was saved
      }
    } catch (error) {
      ui.snackbar.load({
        id: Date.now(),
        type: "danger",
        message: "Failed to submit dispute. Please try again.",
        timeoutInMs: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const updateDispute = async (
    measure: IMeasure,
    reason: string,
    rating?: number,
    disputeType?: string,
    additionalComments?: string,
  ) => {
    try {
      await api.measure.update(
        {
          ...measure,
          disputeReason: reason,
          preferredRating: rating,
          disputeType,
          additionalComments,
          isDispute: true,
        },
        [
          "disputeReason",
          "preferredRating",
          "disputeType",
          "additionalComments",
          "isDispute",
        ],
      );
      const $agreement: IScorecardMetadata = {
        ...agreement,
        midtermRated: true,
        isDispute: true,
      };
      await api.individualScorecard.update($agreement);
    } catch (error) {
      console.error("Error updating dispute:", error);
      throw error;
    }
  };

  const onCancel = () => {
    store.measure.clearSelected();
    setMeasure({ ...defaultMeasure });
    setDisputeReason("");
    setPreferredRating(undefined);
    setDisputeType("");
    setAdditionalComments("");
    setErrors({});
    hideModalFromId(MODAL_NAMES.EXECUTION.SCORECARD_DISPUTE_Q2_MODAL);
  };

  useEffect(() => {
    const selectedMeasure = store.measure.selected;
    if (!selectedMeasure) {
      setMeasure({ ...defaultMeasure });
      setDisputeReason("");
      setPreferredRating(undefined);
      setDisputeType("");
      setAdditionalComments("");
      setErrors({});
    } else {
      setMeasure({ ...defaultMeasure, ...selectedMeasure });
      setDisputeReason(selectedMeasure.disputeReason || "");
      setPreferredRating(selectedMeasure.preferredRating || undefined);
      setDisputeType(selectedMeasure.disputeType || "");
      setAdditionalComments(selectedMeasure.additionalComments || "");
    }
  }, [store.measure.selected]);

  useEffect(() => {
    const uid = store.auth.meJson?.uid;
    const load = async () => {
      if (!uid) return;
      setLoading(true);
      await api.objective.getAll(uid);
      await api.measure.getAll(uid);
      setLoading(false);
    };
    load();
  }, [api.measure, api.objective]);

  useEffect(() => {
    setRemainingChars(maxCharCount - disputeReason.length);

    if (maxCharCount < disputeReason.length) {
      setDisputeReason(disputeReason.substring(0, maxCharCount));
    }
  }, [disputeReason]);

  const disputeExists = measure.isDispute === true;
  return (
    <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-auto my-8 overflow-hidden">
      {/* Header */}
      <div className="bg-[#84cbe9] px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <FileText className="w-6 h-6 text-white" />
          <div>
            <h3 className="text-xl font-semibold text-white">
              {disputeExists
                ? "Edit Scorecard Dispute"
                : "Submit Scorecard Dispute"}
            </h3>
            <p className="text-sm text-white/90">
              {disputeExists
                ? "Update your dispute details"
                : "Provide details for your dispute"}
            </p>
          </div>
        </div>
        <button
          onClick={onCancel}
          className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
          type="button"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Content */}
      <div className="p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Dispute Reason */}
          <div>
            <label
              htmlFor="dispute-reason"
              className="flex items-center gap-2 text-sm font-semibold text-[#3f6a8e] mb-2"
            >
              <AlertCircle className="w-4 h-4" />
              Dispute Reason <span className="text-[#dc3545]">*</span>
            </label>
            <textarea
              id="dispute-reason"
              rows={5}
              placeholder="Explain why you are disputing this rating. Be specific and provide evidence where possible."
              value={disputeReason}
              onChange={(e) => {
                setDisputeReason(e.target.value);
                if (errors.disputeReason) {
                  setErrors({ ...errors, disputeReason: "" });
                }
              }}
              className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#84cbe9] transition-all resize-none ${
                errors.disputeReason
                  ? "border-[#dc3545] bg-red-50"
                  : remainingChar < 50
                    ? "border-[#faa05a] bg-orange-50"
                    : "border-gray-300"
              }`}
            />
            {errors.disputeReason && (
              <p className="mt-2 text-sm text-[#dc3545] flex items-center gap-1">
                <AlertTriangle className="w-4 h-4" />
                {errors.disputeReason}
              </p>
            )}
            {remainingChar < 100 && !errors.disputeReason && (
              <p
                className={`mt-2 text-sm ${remainingChar < 50 ? "text-[#faa05a]" : "text-gray-600"}`}
              >
                {remainingChar} characters remaining
              </p>
            )}
          </div>

          {/* Preferred Rating */}
          <div>
            <label
              htmlFor="preferred-rating"
              className="flex items-center gap-2 text-sm font-semibold text-[#3f6a8e] mb-2"
            >
              <Star className="w-4 h-4" />
              Preferred Rating
            </label>
            <input
              type="number"
              id="preferred-rating"
              min="1"
              max="5"
              step="0.1"
              placeholder="Enter your preferred rating (1-5)"
              value={preferredRating ?? ""}
              onChange={(e) => {
                setPreferredRating(
                  e.target.value ? Number(e.target.value) : undefined,
                );
                if (errors.preferredRating) {
                  setErrors({ ...errors, preferredRating: "" });
                }
              }}
              className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#84cbe9] transition-all ${
                errors.preferredRating
                  ? "border-[#dc3545] bg-red-50"
                  : "border-gray-300"
              }`}
            />
            {errors.preferredRating && (
              <p className="mt-2 text-sm text-[#dc3545] flex items-center gap-1">
                <AlertTriangle className="w-4 h-4" />
                {errors.preferredRating}
              </p>
            )}
          </div>

          {/* Dispute Type */}
          <div>
            <label
              htmlFor="dispute-type"
              className="flex items-center gap-2 text-sm font-semibold text-[#3f6a8e] mb-2"
            >
              <AlertTriangle className="w-4 h-4" />
              Dispute Type <span className="text-[#dc3545]">*</span>
            </label>
            <select
              id="dispute-type"
              value={disputeType}
              onChange={(e) => {
                setDisputeType(e.target.value);
                if (errors.disputeType) {
                  setErrors({ ...errors, disputeType: "" });
                }
              }}
              className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#84cbe9] transition-all ${
                errors.disputeType
                  ? "border-[#dc3545] bg-red-50"
                  : "border-gray-300"
              }`}
            >
              <option value="">Select Dispute Type</option>
              <option value="calculation">Calculation Error</option>
              <option value="data">Data Entry Error</option>
              <option value="bias">Supervisor Bias</option>
              <option value="criteria">Unclear Criteria</option>
              <option value="other">Other</option>
            </select>
            {errors.disputeType && (
              <p className="mt-2 text-sm text-[#dc3545] flex items-center gap-1">
                <AlertTriangle className="w-4 h-4" />
                {errors.disputeType}
              </p>
            )}
          </div>

          {/* Additional Comments */}
          <div>
            <label
              htmlFor="additional-comments"
              className="flex items-center gap-2 text-sm font-semibold text-[#3f6a8e] mb-2"
            >
              <MessageSquare className="w-4 h-4" />
              Additional Comments
            </label>
            <textarea
              id="additional-comments"
              rows={3}
              placeholder="Any additional information that supports your dispute"
              value={additionalComments}
              onChange={(e) => setAdditionalComments(e.target.value)}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#84cbe9] transition-all resize-none"
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onCancel}
              className="px-6 py-2.5 text-[#3f6a8e] font-medium hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2.5 bg-[#84cbe9] text-white font-medium rounded-lg hover:bg-[#3f6a8e] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Submitting...
                </>
              ) : (
                <>{disputeExists ? "Update Dispute" : "Submit Dispute"}</>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
});

export default SubmitScorecardDisputeModal;
